import React, { useEffect, useRef, useMemo } from "react";
import { Stage as PixiStage } from "@pixi/react";
import { ReactReduxContext } from "react-redux";
import { DimensionsContext } from "./Contexts/DimensionsContext";
import { FreeSpinAnimationContext } from "./Contexts/FreeSpinAnimationContext";
import { AppActivityContext } from "./Contexts/AppActivityContext";
import { ReelControlContext } from "./Contexts/ReelControlContext";
import { FreeSpinControlContext } from "./Contexts/FreeSpinControlContext";
import { HoldAndWinControlContext } from "./Contexts/HoldAndWinControlContext";
import { PixiSettings } from "./pixiSettings";
import { useDimensions } from "./Contexts/DimensionsContext";
import ContextBridge from "./Contexts/ContextBridge";
import BackgroundAnimation from "./Components/Animations/BackgroundAnimation/BackgroundAnimation";

// import Menu from "./Components/UserInterface/primary/Menu/Menu";
import IntroPopup from "./Components/UserInterface/shared/Popups/IntroPopup/IntroPopup/IntroPopup";
import IntroTransitionOverlay from "./Components/Animations/IntroTransitionOverlay/IntroTransitionOverlay";
import ErrorRecoveryOverlay from "./Components/Animations/ErrorRecoveryOverlay/ErrorRecoveryOverlay";
import Transitions from "./Components/Animations/Transitions/Transitions";
import GameContainer from "./Games/Main/GameContainer/GameContainer";
import WinAnimation from "./Components/Animations/WinAnimation/WinAnimation";
import IosFullscreenOverlay from "./Components/Animations/IosFullscreenOverlay/IosFullscreenOverlay";
import { FEATURES_CONFIG } from "./featuresConfig";
import Dialogs from "./Components/Animations/Dialogs/Dialogs";
import { ReelRefsContext } from "./Contexts/ReelRefsContext";
import HookImport from "./Hooks/HookImport";
import Loader from "./Components/UserInterface/primary/Popups/Loader";
import { useSelector } from "react-redux";
import { Settings } from "./settings";

// Dynamic UI Theme imports
import PrimaryUserInterface from "./Components/UserInterface/primary/UserInterface";
import SecondaryUserInterface from "./Components/UserInterface/secondary/UserInterface";

const UserInterface = Settings.APP.UI_THEME === "primary"
  ? PrimaryUserInterface
  : SecondaryUserInterface;

const StageWithDimensions = ({ children }) => {
  const { dimensions, device } = useDimensions();
  const stageRef = useRef(null);

  // Memoize contexts array (static array, doesn't change)
  const contexts = useMemo(
    () => [
      { Context: ReelRefsContext },
      { Context: ReactReduxContext },
      { Context: AppActivityContext },
      { Context: DimensionsContext },
      { Context: FreeSpinAnimationContext },
      { Context: ReelControlContext },
      { Context: FreeSpinControlContext },
      { Context: HoldAndWinControlContext },
    ],
    [],
  );

  // Memoize PixiStage options
  const stageOptions = useMemo(
    () => ({
      resolution: device === PixiSettings.DEVICE.MOBILE ? 2 : 1,
      autoDensity: true,
      backgroundColor: 0x000000,
      antialias: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
      clearBeforeRender: true,
    }),
    [device],
  );

  useEffect(() => {
    if (stageRef.current) {
      const app = stageRef.current.app;
      globalThis.__PIXI_APP__ = app;
      globalThis.__PIXI_STAGE__ = app.stage;
      globalThis.__PIXI_RENDERER__ = app.renderer;
      globalThis.__PIXI_STAGE__.name = "stage";
      globalThis.__PIXI_STAGE__.sortableChildren = true;
    }
  }, []);

  return (
    <ContextBridge
      contexts={contexts}
      render={(bridgeChildren) => (
        <PixiStage
          ref={stageRef}
          width={dimensions.width}
          height={dimensions.height}
          options={stageOptions}
          name="stage"
        >
          {bridgeChildren}
        </PixiStage>
      )}
    >
      {children}
    </ContextBridge>
  );
};

const Stage = () => {
  const { loaderComplete } = useSelector((state) => state.loadingProgress);

  return (
    <>
      <StageWithDimensions>
        <HookImport />

        <Loader />

        {/* Game mounts after loader "out" finishes — loader stays frozen on top while game initializes */}
        {loaderComplete && (
          <>
            <BackgroundAnimation />
            <IntroPopup />
            <IntroTransitionOverlay />
            <ErrorRecoveryOverlay />
            <UserInterface>
              <GameContainer />
            </UserInterface>
            <Transitions />
            <Dialogs />
            <WinAnimation />
            {FEATURES_CONFIG.IOS_FULLSCREEN_OVERLAY.ENABLED && <IosFullscreenOverlay />}

          </>
        )}
      </StageWithDimensions>
    </>
  );
};

export default Stage;
